/**
 * Typed client for plan sharing: share links, public shared views,
 * co-signs, and forks.
 *
 * Contract:
 *  - POST   /v1/plans/{id}/share        -> {share_url, rotated} (auth: owner/staff)
 *  - GET    /v1/plans/{id}/share        -> {share_url | null}   (auth: owner/staff)
 *  - DELETE /v1/plans/{id}/share        -> 204                  (auth: owner/staff)
 *  - GET    /v1/shared/{token}          -> SharedPlan           (PUBLIC)
 *  - POST   /v1/shared/{token}/cosign   -> {cosign_count}       (PUBLIC)
 *  - POST   /v1/shared/{token}/fork     -> PlanDetail+assignments (PUBLIC)
 *
 * Defensive posture matches lib/plans.ts: coerce, don't crash.
 */
import { ApiError, apiFetch } from './api';
import { toPlanDetail } from './plans';
import type { PlanDetail } from './plans';

export interface SharedDistrictStat {
  district: number;
  population: number;
  deviation_pct: number;
}

export interface SharedPlan {
  token: string;
  plan_id: string;
  name: string;
  district_count: number;
  status: string;
  assignments: Record<string, number>;
  ideal_population: number;
  max_deviation_pct: number;
  districts: SharedDistrictStat[];
  cosign_count: number;
  fork_count: number;
  author_display_name: string | null;
  /** The author's explanation of the plan. */
  comments: string | null;
  /** The author's district names ("" = default); null when none. */
  district_labels: string[] | null;
  created_at: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toDistrictIndex(value: unknown, districtCount: number): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    // API is 1-based; the map paints 0-based.
    if (value >= 1 && value <= districtCount) return value - 1;
    if (value >= 0 && value < districtCount) return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isInteger(parsed)) {
      if (parsed >= 1 && parsed <= districtCount) return parsed - 1;
      if (parsed >= 0 && parsed < districtCount) return parsed;
    }
  }
  return null;
}

function toSharedPlan(value: unknown): SharedPlan {
  const record = isRecord(value) ? value : {};
  const districtCount = toNumber(record.district_count, 0);
  const assignments: Record<string, number> = {};
  if (isRecord(record.assignments)) {
    for (const [geoid, district] of Object.entries(record.assignments)) {
      const index = toDistrictIndex(district, districtCount);
      if (index !== null) assignments[geoid] = index;
    }
  }
  const districts: SharedDistrictStat[] = Array.isArray(record.districts)
    ? record.districts.flatMap((d) => {
        if (!isRecord(d)) return [];
        return [
          {
            district: toNumber(d.district, 0),
            population: toNumber(d.population, 0),
            deviation_pct: toNumber(d.deviation_pct, 0),
          },
        ];
      })
    : [];
  return {
    token: toString(record.token, ''),
    plan_id: toString(record.plan_id, ''),
    name: toString(record.name, 'Shared plan'),
    district_count: districtCount,
    status: toString(record.status, 'draft'),
    assignments,
    ideal_population: toNumber(record.ideal_population, 0),
    max_deviation_pct: toNumber(record.max_deviation_pct, 0),
    districts,
    cosign_count: toNumber(record.cosign_count, 0),
    fork_count: toNumber(record.fork_count, 0),
    author_display_name:
      typeof record.author_display_name === 'string' ? record.author_display_name : null,
    comments: typeof record.comments === 'string' && record.comments.trim() ? record.comments : null,
    district_labels: Array.isArray(record.district_labels)
      ? record.district_labels.map((v) => (typeof v === 'string' ? v : ''))
      : null,
    created_at: toString(record.created_at, ''),
  };
}

/** POST /v1/plans/{id}/share — create or rotate the share link. */
export async function sharePlan(planId: string): Promise<{ share_url: string; rotated: boolean }> {
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/share`, {
    method: 'POST',
  });
  const record = isRecord(payload) ? payload : {};
  return {
    share_url: toAppUrl(toString(record.share_url, '')),
    rotated: record.rotated === true,
  };
}

/** GET /v1/plans/{id}/share — current share link, if any. */
export async function getShareInfo(planId: string): Promise<{ share_url: string | null }> {
  const payload = await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/share`);
  const record = isRecord(payload) ? payload : {};
  return {
    share_url: typeof record.share_url === 'string' ? toAppUrl(record.share_url) : null,
  };
}

/** DELETE /v1/plans/{id}/share — revoke the share link. */
export async function revokeShare(planId: string): Promise<void> {
  await apiFetch<unknown>(`/v1/plans/${encodeURIComponent(planId)}/share`, {
    method: 'DELETE',
  });
}

/** GET /v1/shared/{token} — public shared-plan view. */
export async function getSharedPlan(token: string): Promise<SharedPlan> {
  const payload = await apiFetch<unknown>(`/v1/shared/${encodeURIComponent(token)}`);
  return toSharedPlan(payload);
}

/** Thrown when the contact already co-signed (HTTP 409). */
export class CosignDuplicateError extends Error {
  constructor() {
    super('This contact has already co-signed this plan.');
    this.name = 'CosignDuplicateError';
  }
}

/**
 * POST /v1/shared/{token}/cosign — co-sign a shared plan.
 * Returns the updated co-sign count.
 */
export async function cosignPlan(
  token: string,
  displayName: string,
  contact?: string,
): Promise<number> {
  try {
    const payload = await apiFetch<unknown>(`/v1/shared/${encodeURIComponent(token)}/cosign`, {
      method: 'POST',
      body: JSON.stringify({
        display_name: displayName,
        ...(contact?.trim() ? { contact: contact.trim() } : {}),
      }),
    });
    const record = isRecord(payload) ? payload : {};
    return toNumber(record.cosign_count, 0);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) throw new CosignDuplicateError();
    throw error;
  }
}

/**
 * POST /v1/shared/{token}/fork — copy a shared plan into your own editable
 * draft. Returns the full plan detail (assignments included) so the caller
 * can load it into the editor without a second fetch.
 */
export async function forkPlan(token: string, districtCount: number): Promise<PlanDetail> {
  const payload = await apiFetch<unknown>(`/v1/shared/${encodeURIComponent(token)}/fork`, {
    method: 'POST',
  });
  return toPlanDetail(payload, districtCount, undefined);
}

/**
 * Share links always open in this app. The API builds them from its own
 * Host header, which in local dev is the API port (:8000) over https — a
 * dead link. Rebase the path onto the app's origin; in production the app
 * and API share a host, so this is a no-op there.
 */
export function toAppUrl(url: string): string {
  if (!url || typeof window === 'undefined') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}
